import Link from '@/components/Link/Link';

import { stateLabels, type statePaths } from './stateInfo';
import { inactiveColor } from './styleConstants';

const baseProps = {
  stroke: '#ffffff',
  strokeOpacity: 1,
  strokeLinejoin: 'miter' as const,
  strokeLinecap: 'square' as const,
  strokeMiterlimit: 4,
  strokeWidth: 2,
  strokeDasharray: -2,
};

const baseStyleProps = {
  WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
  strokeOpacity: 1,
  strokeLinejoin: 'miter' as const,
  strokeLinecap: 'square' as const,
  strokeMiterlimit: 4,
  cursor: 'auto',
};

const activeBaseProps = {
  ...baseProps,
  style: {
    ...baseStyleProps,
  },
};

const inactiveProps = {
  ...activeBaseProps,
  fill: inactiveColor,
};

export enum StateType {
  SOLAR_ONLY = 'SOLAR_ONLY',
  INACTIVE = 'INACTIVE',
  SOLAR_AND_BATTERY = 'SOLAR_AND_BATTERY',
}

type BaseProps = {
  path: string;
  state: keyof typeof statePaths;
};

interface ActiveProps extends BaseProps {
  color: string;
  type: StateType.SOLAR_AND_BATTERY | StateType.SOLAR_ONLY;
  link: {
    href?: string; // Only optional for VT and AZ
    setHoveredState: (state: string | undefined) => void;
  };
}

interface InactiveProps extends BaseProps {
  type: StateType.INACTIVE;
}

const State = (props: ActiveProps | InactiveProps) => {
  if (props.type === StateType.INACTIVE) {
    return <path d={props.path} {...inactiveProps} />;
  }

  const { state, path, link, color } = props;

  // biome-ignore lint/correctness/noNestedComponentDefinitions: closes over per-render props
  // biome-ignore lint/correctness/noComponentHookFactories: closes over per-render props
  const ActiveState = () => (
    <path
      d={path}
      {...{
        ...activeBaseProps,
        style: {
          ...activeBaseProps.style,
          cursor: link?.href ? 'pointer' : 'auto', // Only needed for VT and AZ cases
        },
      }}
      fill={color}
    />
  );

  return link.href && Object.keys(stateLabels).includes(state) ? (
    <Link
      href={link.href}
      aria-label={stateLabels[state].stateName}
      style={{ WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)' }}
      onMouseEnter={() => link.setHoveredState(state)}
      onMouseLeave={() => link.setHoveredState(undefined)}
    >
      <ActiveState />
    </Link>
  ) : (
    // biome-ignore lint/a11y/noStaticElementInteractions: Automated migration to Biome
    <g
      aria-label={stateLabels[state].stateName}
      onMouseEnter={() => link.setHoveredState(state)}
      onMouseLeave={() => link.setHoveredState(undefined)}
    >
      <ActiveState />
    </g>
  );
};

export default State;
